/**
 * 시스템 소개 — **슬라이드를 문서처럼 세로로 내려 읽는다.**
 *
 * 내려받는 소개서(PDF·PPT)와 같은 장들이다(`manual/export/overview.ts`). 화면에서는 넘기는
 * 대신 스크롤한다 — 고객 앞에서 한 장씩 짚어 가며 보여 주거나, 주소 하나로 읽게 하는 자리다.
 *
 * 장 안의 치수는 **슬라이드 폭에 비례**한다(`cqw`). PDF 와 같은 960 × 540pt 좌표를 쓰므로
 * 창이 넓든 좁든 PDF 쪽과 같은 모양으로 보이고, 한 장 안에서 글이 줄을 다르게 바꾸지 않는다.
 */
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileDown, Presentation } from 'lucide-react';
import {
  OVERVIEW,
  type ClosingSlide,
  type CoverSlide,
  type FactsSlide,
  type FeatureSlide,
  type NumbersSlide,
  type OverviewSlide,
  type StepsSlide,
} from '../manual/export/overview.js';
import { shotUrl } from '../manual/ScreenGuide.js';
import { DOWNLOADS } from '../manual/downloads.js';
import { downloadHref, downloadName } from '../components/ManualDownload.js';

/** 슬라이드 좌표(pt, 폭 960) → 슬라이드 폭에 비례하는 길이. PDF 조판(`pdf.ts`)과 같은 수를 쓴다. */
const pt = (n: number): string => `${((n / 960) * 100).toFixed(4)}cqw`;

/* PDF 와 같은 색 — 콘솔 토큰에 없는 둘(라임 글씨·옅은 판)만 여기 둔다. */
const LIME_INK = '#4d7d0f';
const PAPER = '#f5f7fa';
const MUTED = '#637387';
const ON_NAVY = '#b8c9db';

/** 글 크기와 줄 간격. 제목은 1.3, 본문은 1.55 — PDF 와 같다. */
const text = (size: number, leading = 1.55): CSSProperties => ({ fontSize: pt(size), lineHeight: leading });

/** 라임 막대 — 장 머리와 표지의 표식. */
function Bar({ width = 24 }: { width?: number }): ReactNode {
  return <span className="block bg-th-5" style={{ width: pt(width), height: pt(3) }} />;
}

/** 장 머리의 작은 이름. */
function Kicker({ label }: { label: string }): ReactNode {
  return (
    <>
      <Bar />
      <p className="font-bold" style={{ ...text(12), marginTop: pt(9), color: LIME_INK }}>
        {label}
      </p>
    </>
  );
}

/** 장 머리 — 작은 이름과 큰 제목. */
function Header({ label, headline }: { label: string; headline: string }): ReactNode {
  return (
    <div>
      <Kicker label={label} />
      <h2 className="font-bold tracking-tight text-th-3" style={{ ...text(28, 1.3), marginTop: pt(2) }}>
        {headline}
      </h2>
    </div>
  );
}

/** 콘솔 화면 그림 — 흰 화면이 흰 장에 묻히지 않게 그림자를 깐다. */
function Shot({
  id,
  alt,
  style,
  dark = false,
}: {
  id: string;
  alt: string;
  style: CSSProperties;
  dark?: boolean;
}): ReactNode {
  const url = shotUrl(id);
  if (!url) return null;
  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      className={`absolute ${dark ? 'ring-1 ring-white/20' : 'ring-1 ring-slate-200'}`}
      style={{ ...style, borderRadius: pt(4), boxShadow: `0 ${pt(6)} ${pt(22)} rgb(40 59 81 / 0.16)` }}
    />
  );
}

/** 흰 장의 바닥글 — 무엇을 보고 있는지와 몇 번째 장인지. */
function Footer({ number }: { number: number }): ReactNode {
  return (
    <div
      className="absolute flex justify-between"
      style={{ ...text(8.5, 1), left: pt(56), right: pt(56), bottom: pt(22), color: MUTED }}
    >
      <span>
        {OVERVIEW.product} · {OVERVIEW.tagline}
      </span>
      <span>
        {number} / {OVERVIEW.slides.length}
      </span>
    </div>
  );
}

/* ────────────────────────────── 장 ────────────────────────────── */

function Cover({ slide }: { slide: CoverSlide }): ReactNode {
  return (
    <>
      <div
        className="absolute flex flex-col justify-center"
        style={{ left: pt(56), top: 0, bottom: 0, width: pt(320) }}
      >
        <Bar width={36} />
        <p className="font-bold text-white" style={{ ...text(40, 1.3), marginTop: pt(15) }}>
          {OVERVIEW.product}
        </p>
        <p style={{ ...text(15), color: '#a8c766' }}>{OVERVIEW.tagline}</p>
        <h2 className="font-bold tracking-tight text-white" style={{ ...text(34, 1.3), marginTop: pt(34) }}>
          {slide.headline}
        </h2>
        <p style={{ ...text(13.5), marginTop: pt(14), color: ON_NAVY }}>{slide.sub}</p>
      </div>
      <Shot
        id={slide.shot}
        alt="대시보드 화면"
        dark
        style={{ left: pt(416), width: pt(488), top: '50%', transform: 'translateY(-50%)' }}
      />
      <p className="absolute" style={{ ...text(9.5, 1), left: pt(56), bottom: pt(28), color: ON_NAVY }}>
        {OVERVIEW.supplier}
      </p>
    </>
  );
}

function Numbers({ slide }: { slide: NumbersSlide }): ReactNode {
  return (
    <div className="absolute inset-0 flex flex-col" style={{ padding: `${pt(56)} ${pt(56)} ${pt(50)}` }}>
      <Header label={slide.label} headline={slide.headline} />
      <div className="flex flex-1 items-center">
        <div className="grid w-full" style={{ gridTemplateColumns: `repeat(${slide.items.length}, 1fr)`, gap: pt(20) }}>
          {slide.items.map((item) => (
            <div key={item.label} style={{ height: pt(206), padding: pt(24), borderRadius: pt(14), background: PAPER }}>
              <span className="block bg-th-5" style={{ width: pt(24), height: pt(3), marginTop: pt(4) }} />
              <p className="font-bold text-th-3" style={{ ...text(52, 1.3), marginTop: pt(12) }}>
                {item.value}
              </p>
              <p className="font-bold" style={{ ...text(17), marginTop: pt(4), color: LIME_INK }}>
                {item.label}
              </p>
              <p style={{ ...text(12), marginTop: pt(2), color: MUTED }}>{item.note}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Feature({ slide }: { slide: FeatureSlide }): ReactNode {
  return (
    <>
      <div className="absolute" style={{ left: pt(340), right: 0, top: 0, bottom: 0, background: PAPER }} />
      <Shot
        id={slide.shot}
        alt={`${slide.label} 화면`}
        style={{ left: pt(368), width: pt(536), top: `calc(50% - ${pt(6)})`, transform: 'translateY(-50%)' }}
      />
      <div
        className="absolute flex flex-col justify-center"
        style={{ left: pt(56), width: pt(256), top: 0, bottom: pt(12) }}
      >
        <Kicker label={slide.label} />
        <h2 className="font-bold tracking-tight text-th-3" style={{ ...text(28, 1.3), marginTop: pt(2) }}>
          {slide.headline}
        </h2>
        <p style={{ ...text(13), marginTop: pt(14), color: MUTED }}>{slide.sub}</p>
        <ul className="flex flex-col items-start" style={{ marginTop: pt(22), gap: pt(10) }}>
          {slide.chips.map((chip) => (
            <li
              key={chip}
              className="inline-flex items-center rounded-full bg-th-2 font-bold"
              style={{
                ...text(12, 1),
                height: pt(30),
                padding: `0 ${pt(16)} 0 ${pt(14)}`,
                gap: pt(10),
                color: LIME_INK,
              }}
            >
              <span className="rounded-full bg-th-5" style={{ width: pt(6), height: pt(6) }} />
              {chip}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

function Facts({ slide }: { slide: FactsSlide }): ReactNode {
  return (
    <div className="absolute inset-0 flex flex-col" style={{ padding: `${pt(56)} ${pt(56)} ${pt(50)}` }}>
      <Header label={slide.label} headline={slide.headline} />
      <div className="flex flex-1 items-center" style={{ gap: pt(44) }}>
        <div className="grid shrink-0 grid-cols-2" style={{ width: pt(448), gap: pt(14) }}>
          {slide.items.map((item) => (
            <div
              key={item.title}
              className="relative flex flex-col justify-center"
              style={{ height: pt(72), padding: `0 ${pt(16)} 0 ${pt(20)}`, borderRadius: pt(12), background: PAPER }}
            >
              <span className="absolute left-0 bg-th-5" style={{ top: pt(14), bottom: pt(14), width: pt(3) }} />
              <p className="font-bold text-th-3" style={text(14)}>
                {item.title}
              </p>
              <p style={{ ...text(11.5), color: MUTED }}>{item.text}</p>
            </div>
          ))}
        </div>
        {/* 기기 화면 — 검은 테두리와 받침을 둘러 "모니터에 뜬 것" 으로 보이게 한다. */}
        <figure className="flex flex-1 flex-col items-center">
          <div className="w-full" style={{ padding: pt(10), borderRadius: pt(12), background: '#0f141c' }}>
            {shotUrl(slide.shot) ? (
              <img src={shotUrl(slide.shot)} alt="기기 화면의 등록 코드" loading="lazy" className="block w-full" />
            ) : null}
          </div>
          <span className="block" style={{ width: pt(44), height: pt(12), background: '#ccd4de' }} />
          <span className="block" style={{ width: pt(128), height: pt(4), background: '#ccd4de' }} />
          <figcaption className="text-center" style={{ ...text(11.5), marginTop: pt(12), color: MUTED }}>
            {slide.caption}
          </figcaption>
        </figure>
      </div>
    </div>
  );
}

function Steps({ slide }: { slide: StepsSlide }): ReactNode {
  const column = (960 - 56 * 2) / slide.items.length;
  return (
    <>
      <div className="absolute" style={{ left: pt(56), right: pt(56), top: pt(56) }}>
        <Header label={slide.label} headline={slide.headline} />
        <p style={{ ...text(13), marginTop: pt(6), color: MUTED }}>{slide.sub}</p>
      </div>
      {/* 동그라미를 잇는 선 — 첫 동그라미 가운데에서 마지막 동그라미 가운데까지. */}
      <span
        className="absolute bg-slate-200"
        style={{ left: pt(56 + column / 2), right: pt(56 + column / 2), top: pt(540 - 232 - 1), height: pt(2) }}
      />
      <ol
        className="absolute grid"
        style={{
          left: pt(56),
          right: pt(56),
          top: pt(540 - 232 - 30),
          gridTemplateColumns: `repeat(${slide.items.length}, 1fr)`,
        }}
      >
        {slide.items.map((item, index) => {
          const last = index === slide.items.length - 1;
          return (
            <li key={item.title} className="flex flex-col items-center text-center">
              <span
                className={`flex items-center justify-center rounded-full font-bold ${last ? 'bg-th-5 text-th-3' : 'bg-th-3 text-white'}`}
                style={{ ...text(22, 1), width: pt(60), height: pt(60) }}
              >
                {index + 1}
              </span>
              <p className="font-bold text-th-3" style={{ ...text(15), marginTop: pt(20) }}>
                {item.title}
              </p>
              <p style={{ ...text(12), color: MUTED, padding: `0 ${pt(8)}` }}>{item.text}</p>
            </li>
          );
        })}
      </ol>
    </>
  );
}

function Closing({ slide }: { slide: ClosingSlide }): ReactNode {
  return (
    <>
      <div className="absolute" style={{ left: pt(56), top: pt(147), width: pt(768) }}>
        <Bar width={36} />
        <h2 className="font-bold tracking-tight text-white" style={{ ...text(34, 1.3), marginTop: pt(17) }}>
          {slide.headline}
        </h2>
        <p style={{ ...text(15), marginTop: pt(12), color: ON_NAVY }}>{slide.sub}</p>
        <p className="font-bold text-th-5" style={{ ...text(12), marginTop: pt(40) }}>
          {slide.itemsTitle}
        </p>
        <ul className="flex flex-wrap" style={{ marginTop: pt(6), gap: pt(12) }}>
          {slide.items.map((item) => (
            <li
              key={item}
              className="inline-flex items-center rounded-full bg-white/10 text-white"
              style={{ ...text(13, 1), height: pt(34), padding: `0 ${pt(16)}` }}
            >
              {item}
            </li>
          ))}
        </ul>
      </div>
      <div
        className="absolute flex items-end justify-between border-t"
        style={{ left: pt(56), right: pt(56), bottom: pt(40), paddingTop: pt(22), borderColor: '#4d6179' }}
      >
        <p className="flex items-baseline" style={{ gap: pt(14) }}>
          <span className="font-bold text-white" style={text(18, 1)}>
            {OVERVIEW.product}
          </span>
          <span style={{ ...text(11, 1), color: ON_NAVY }}>{OVERVIEW.supplier}</span>
        </p>
        <p style={{ ...text(9, 1), color: ON_NAVY }}>{OVERVIEW.footnote}</p>
      </div>
    </>
  );
}

function Slide({ slide, number }: { slide: OverviewSlide; number: number }): ReactNode {
  const dark = slide.kind === 'cover' || slide.kind === 'closing';
  return (
    <section
      aria-label={`${number} / ${OVERVIEW.slides.length}`}
      className={`relative aspect-video w-full overflow-hidden break-keep rounded-xl shadow-sm ring-1 [container-type:inline-size] [text-wrap:balance] ${
        dark ? 'bg-th-3 ring-th-3' : 'bg-white ring-slate-200'
      }`}
      style={{ color: '#1f2938' }}
    >
      {slide.kind === 'cover' && <Cover slide={slide} />}
      {slide.kind === 'numbers' && <Numbers slide={slide} />}
      {slide.kind === 'feature' && <Feature slide={slide} />}
      {slide.kind === 'facts' && <Facts slide={slide} />}
      {slide.kind === 'steps' && <Steps slide={slide} />}
      {slide.kind === 'closing' && <Closing slide={slide} />}
      {!dark && <Footer number={number} />}
    </section>
  );
}

export function OverviewScreen(): ReactNode {
  const { current } = DOWNLOADS;
  const file = (id: string, label: string, icon: ReactNode): ReactNode => {
    const entry = current.files[id];
    if (!entry) return null;
    return (
      <a
        href={downloadHref(entry)}
        download={downloadName(entry, current.builtAt)}
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-700 transition-colors hover:bg-slate-50"
      >
        {icon}
        {label}
      </a>
    );
  };

  return (
    <div className="mx-auto max-w-[1120px] space-y-6 pb-12">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/manual"
          className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-[13px] text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2} />
          사용 설명서
        </Link>
        <p className="text-[13px] text-slate-400">슬라이드 {OVERVIEW.slides.length}장 · 아래로 내려 읽습니다</p>
        <div className="ml-auto flex gap-2">
          {file('overview-pdf', 'PDF', <FileDown className="h-4 w-4" strokeWidth={2} />)}
          {file('overview-pptx', 'PPT', <Presentation className="h-4 w-4" strokeWidth={2} />)}
        </div>
      </div>
      {OVERVIEW.slides.map((slide, index) => (
        <Slide key={`${slide.kind}-${index}`} slide={slide} number={index + 1} />
      ))}
    </div>
  );
}
